import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { signatureService } from '../services/signatureService';

interface EntityService {
  validate(entity: any, techNotes: Record<string, any>, selectedPrestataire: number | null): Promise<void>;
  refuse(id: string): Promise<void>;
  delete(id: string): Promise<void>;
  launchConsultation(id: string, entity: any): Promise<void>;
}

interface ActionsConfig {
  service: EntityService;
  entityType: 'operation' | 'commande';
  redirectRoute: '/operations' | '/commandes';
}

export const useSuiviEntityActions = (config: ActionsConfig) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isRefuseModalOpen, setIsRefuseModalOpen] = useState(false);
  const [isSupprimerModalOpen, setIsSupprimerModalOpen] = useState(false);

  const handleRefuseEntity = async () => {
    try {
      await config.service.refuse(id!);
      window.location.reload();
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors du refus de l'${config.entityType}.`);
    }
  };

  const handleDeleteEntity = async () => {
    try {
      await config.service.delete(id!);
      navigate(config.redirectRoute);
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors de la suppression de l'${config.entityType}.`);
    }
  };

  const handleFinaliserEntity = async (
    entity: any,
    techNotes: Record<string, any>,
    selectedPrestataire: number | null,
    id_signataire?: number,
    type_signataire?: string
  ) => {
    try {
      // Si id_signataire et type_signataire sont fournis, les ajouter à l'entité
      if (id_signataire !== undefined && type_signataire !== undefined) {
        entity = {
          ...entity,
          id_signataire,
          type_signataire
        };
      }

      // Calcul des dates d'opération si nécessaire
      const calculerDates = () => {
        const today = new Date();
        const dateFinConsult = new Date(today);
        dateFinConsult.setDate(today.getDate() + 10); // 10 jours de consultation
        
        const dateDebutOp = new Date(dateFinConsult);
        dateDebutOp.setDate(dateFinConsult.getDate() + 1);
        
        const delaisOperation = entity.formulaire?.delaisFinOperation || 30;
        const dateFinOp = new Date(dateDebutOp);
        dateFinOp.setDate(dateDebutOp.getDate() + delaisOperation);
        
        return {
          dateDebutOperation: dateDebutOp.toISOString().split('T')[0],
          dateFinOperation: dateFinOp.toISOString().split('T')[0]
        };
      };
      
      // Vérifier et ajouter des dates si elles sont manquantes
      if (!entity.date_debut_operation || entity.date_debut_operation === '') {
        const dates = calculerDates();
        entity.date_debut_operation = dates.dateDebutOperation;
      }
      
      if (!entity.date_fin_operation || entity.date_fin_operation === '') {
        const dates = calculerDates();
        entity.date_fin_operation = dates.dateFinOperation;
      }
      
      console.log("Entity avant validation avec dates:", {
        id_signataire: entity.id_signataire,
        type_signataire: entity.type_signataire,
        date_debut_operation: entity.date_debut_operation,
        date_fin_operation: entity.date_fin_operation
      });

      await config.service.validate(entity, techNotes, selectedPrestataire);

      if (selectedPrestataire !== null) {
        const entities = config.entityType === 'operation'
          ? entity.commandes
          : entity.prestataires;

        if (entities?.[selectedPrestataire]) {
          // Adapter les données pour la signature
          const adaptedEntity = config.entityType === 'commande' ? {
            ...entity,
            commandes: entity.prestataires,
            organisme: entity.organisme,
            nom: entity.titre
          } : entity;

          await signatureService.createSignature(adaptedEntity, selectedPrestataire);
        }
      }

      navigate(`${config.redirectRoute}/${entity.id}?tab=3`);
      window.location.reload();
    } catch (error) {
      console.error('Erreur lors de la finalisation:', error);
      alert(`Impossible de finaliser l'${config.entityType}.`);
    }
  };

  const handleLancerConsultation = async (entity: any) => {
    try {
      await config.service.launchConsultation(id!, entity);

      // Message différent selon le type d'entité
      if (config.service.constructor.name.includes('OperationBC')) {
        alert("La consultation BC est lancée. Le prestataire de rang 1 est informé.");
      } else {
        alert("La consultation est lancée. Les prestataires sont informés.");
      }

      window.location.href = `${window.location.pathname}?tab=2`;
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du lancement de la consultation. Veuillez réessayer.');
    }
  };

  return {
    isRefuseModalOpen,
    setIsRefuseModalOpen,
    isSupprimerModalOpen,
    setIsSupprimerModalOpen,
    handleRefuseEntity,
    handleDeleteEntity,
    handleFinaliserEntity,
    handleLancerConsultation
  };
};